import { signInAnonymously, signInWithEmailAndPassword, createUserWithEmailAndPassword, updatePassword, onAuthStateChanged, type User } from 'firebase/auth'
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore'

export interface UserProfile {
  uid: string
  email: string | null
  displayName: string | null
  isAnonymous: boolean
  createdAt: Date
  lastActiveAt: Date
  sessionStartTime?: Date
}

export const useAuth = () => {
  const { $firebaseAuth, $firestore } = useNuxtApp()
  
  const currentUser = ref<User | null>(null)
  const userProfile = ref<UserProfile | null>(null)
  const sessionStartTime = ref<Date | null>(null)
  const showUpgradeDialog = ref(false)

  // Get temporary anonymous account
  const getTemporaryAccount = async () => {
    try {
      const result = await signInAnonymously($firebaseAuth)
      sessionStartTime.value = new Date()
      
      // Start 5-minute timer for upgrade dialog
      setTimeout(() => {
        if (currentUser.value?.isAnonymous) {
          showUpgradeDialog.value = true
        }
      }, 20 * 1000) // 15 minutes
      
      // Wait for auth state to sync with store
      await new Promise<void>((resolve) => {
        const authStore = useAuthStore()
        let unsubscribe: (() => void) | null = null
        
        unsubscribe = watch(
          () => authStore.currentUser,
          (newUser) => {
            if (newUser && newUser.id === result.user.uid) {
              if (unsubscribe) unsubscribe()
              resolve()
            }
          },
          { immediate: true }
        )
        
        // Fallback timeout
        setTimeout(() => {
          if (unsubscribe) unsubscribe()
          resolve()
        }, 1000)
      })
      
      return result.user
    } catch (error) {
      console.error('Error creating anonymous account:', error)
      throw error
    }
  }

  // Sign up with email and password
  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      const result = await createUserWithEmailAndPassword($firebaseAuth, email, password)
      
      // Create profile in Firestore
      await createUserProfile(result.user, displayName)
      
      showUpgradeDialog.value = false
      return result.user
    } catch (error) {
      console.error('Error signing up:', error)
      throw error
    }
  }

  // Sign in with email and password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword($firebaseAuth, email, password)
      return result.user
    } catch (error) {
      console.error('Error signing in:', error)
      throw error
    }
  }

  // Update password
  const updateUserPassword = async (newPassword: string) => {
    if (!currentUser.value) throw new Error('No user signed in')
    
    try {
      await updatePassword(currentUser.value, newPassword)
    } catch (error) {
      console.error('Error updating password:', error)
      throw error
    }
  }

  // Create user profile in Firestore
  const createUserProfile = async (user: User, displayName?: string) => {
    const profileData: Omit<UserProfile, 'uid'> = {
      email: user.email,
      displayName: displayName || user.displayName,
      isAnonymous: user.isAnonymous,
      createdAt: new Date(),
      lastActiveAt: new Date(),
      sessionStartTime: sessionStartTime.value || new Date()
    }

    await setDoc(doc($firestore, 'profiles', user.uid), profileData)
  }

  // Load user profile from Firestore
  const loadUserProfile = async (uid: string) => {
    try {
      const docSnap = await getDoc(doc($firestore, 'profiles', uid))
      if (docSnap.exists()) {
        const data = docSnap.data()
        userProfile.value = {
          uid,
          ...data,
          createdAt: data.createdAt.toDate(),
          lastActiveAt: data.lastActiveAt.toDate(),
          sessionStartTime: data.sessionStartTime?.toDate()
        } as UserProfile
      }
    } catch (error) {
      console.error('Error loading user profile:', error)
    }
  }

  // Update user profile
  const updateUserProfile = async (updates: Partial<Pick<UserProfile, 'displayName'>>) => {
    if (!currentUser.value) throw new Error('No user signed in')
    
    try {
      await updateDoc(doc($firestore, 'profiles', currentUser.value.uid), {
        ...updates,
        lastActiveAt: new Date()
      })
      
      // Update local profile
      if (userProfile.value) {
        Object.assign(userProfile.value, updates)
        userProfile.value.lastActiveAt = new Date()
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      throw error
    }
  }

  // Initialize auth state listener
  const initializeAuth = () => {
    const authStore = useAuthStore()
    
    onAuthStateChanged($firebaseAuth, async (user) => {
      currentUser.value = user
      
      // Sync with auth store
      authStore.setFirebaseUser(user)
      
      if (user) {
        await loadUserProfile(user.uid)
        
        // Create profile if it doesn't exist (for existing users)
        if (!userProfile.value && !user.isAnonymous) {
          await createUserProfile(user)
          await loadUserProfile(user.uid)
        }
        
        // Reinitialize projects with the current user
        const { useProjectsStore } = await import('~/stores/projects')
        const projectsStore = useProjectsStore()
        projectsStore.initializeProjects(user.uid)
        
        // If user is anonymous and we have an existing session, start the upgrade timer
        if (user.isAnonymous && !sessionStartTime.value) {
          console.log('Starting upgrade timer for existing anonymous user')
          sessionStartTime.value = new Date()
          setTimeout(() => {
            if (currentUser.value?.isAnonymous) {
              console.log('Showing upgrade dialog after 15 minutes')
              showUpgradeDialog.value = true
            }
          }, 15 * 60 * 1000) // 15 minutes
        }
      } else {
        userProfile.value = null
      }
    })
  }

  return {
    currentUser: readonly(currentUser),
    userProfile: readonly(userProfile),
    showUpgradeDialog,
    getTemporaryAccount,
    signUpWithEmail,
    signInWithEmail,
    updateUserPassword,
    updateUserProfile,
    initializeAuth,
    dismissUpgradeDialog: () => { showUpgradeDialog.value = false }
  }
}